import React, { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../config/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag, faTimes } from "@fortawesome/free-solid-svg-icons";
import { AVAILABLE_TAGS } from "../constants/tagConstants";
import { useTranslation } from "react-i18next";

const VehicleTags = ({ vehicle, user, onTagsUpdated }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);
  const [localTags, setLocalTags] = useState(vehicle.tags || []);
  const { t } = useTranslation();

  // Keep localTags in sync with vehicle.tags
  useEffect(() => {
    setLocalTags(vehicle.tags || []);
  }, [vehicle.tags]);

  const handleAddTag = async (tagType) => {
    try {
      setError(null);
      const response = await fetch(API_ENDPOINTS.ADD_VEHICLE_TAG(vehicle._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ tag: tagType }),
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        throw new Error("Invalid server response");
      }

      if (!response.ok) {
        throw new Error(data?.msg || data?.message || "Failed to add tag");
      }

      if (!data) {
        throw new Error("No data received from server");
      }

      // Update local state immediately
      setLocalTags(data.tags);
      // Update parent component
      onTagsUpdated(data);
      setIsAdding(false);
    } catch (err) {
      console.error("Error adding tag:", err);
      setError(err.message || "An error occurred while adding the tag");
    }
  };

  const handleRemoveTag = async (tagType) => {
    try {
      setError(null);
      const response = await fetch(
        API_ENDPOINTS.REMOVE_VEHICLE_TAG(vehicle._id, tagType),
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        throw new Error("Invalid server response");
      }

      if (!response.ok) {
        throw new Error(data?.msg || data?.message || "Failed to remove tag");
      }

      if (!data) {
        throw new Error("No data received from server");
      }

      // Update local state immediately
      setLocalTags(data.tags);
      // Update parent component
      onTagsUpdated(data);
    } catch (err) {
      console.error("Error removing tag:", err);
      setError(err.message || "An error occurred while removing the tag");
    }
  };

  const existingTags = localTags;
  const availableTags = AVAILABLE_TAGS.filter(
    (tag) => !existingTags.find((t) => t.type === tag)
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faTag} className="text-gray-500" />
        <h3 className="text-lg font-semibold">{t("tags.title")}</h3>
        <div className="relative group">
          <svg
            className="w-4 h-4 text-gray-400 cursor-help"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            {t("tags.help_text")}{" "}
            <a
              href="https://discord.gg/kDN8mkaKAG"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
              onClick={(e) => e.stopPropagation()}
            >
              {t("footer.join_discord")}
            </a>
            <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-gray-800 transform rotate-45"></div>
          </div>
        </div>
      </div>

      {error && <div className="text-red-600 text-sm mb-2">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {existingTags.map((tag) => (
          <div
            key={tag.type}
            className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm flex items-center gap-2 dark:bg-primary-900 dark:text-primary-100"
          >
            {t(`tags.labels.${tag.type}`)}
            {user && (
              <button
                onClick={() => handleRemoveTag(tag.type)}
                className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        ))}

        {user && !isAdding && availableTags.length > 0 && (
          <button
            onClick={() => setIsAdding(true)}
            className="border-2 border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 px-3 py-1 rounded-full text-sm dark:border-gray-500 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-400"
          >
            + {t("tags.add")}
          </button>
        )}

        {isAdding && (
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                onClick={() => handleAddTag(tag)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
              >
                {t(`tags.labels.${tag}`)}
              </button>
            ))}
            <button
              onClick={() => setIsAdding(false)}
              className="text-gray-500 hover:text-gray-700 text-sm dark:text-gray-400 dark:hover:text-gray-200"
            >
              {t("tags.cancel")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleTags;
